/**
 * 統一プロンプトプレセット 新規作成 / 編集ダイアログ。
 * scope は親 template から自動継承（UI には表示しない）。
 */
import { useEffect, useMemo, useState } from 'react';

import type { PromptPreset, PromptScope } from '@goenhub/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import {
  useCreatePromptPreset,
  usePromptTemplates,
  useUpdatePromptPreset,
  type PromptPresetRecord,
} from '@/hooks/usePromptData';

interface PresetFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 編集時のみ指定 */
  initial?: PromptPresetRecord;
  /** Generate 画面からの「プレセット保存」用に現在の入力値を流し込む */
  seed?: { templateId: string; values: Record<string, string> };
  /** 親画面のスコープ。テンプレ選択肢を絞り込み、生成 preset の scope に使う */
  scope?: PromptScope;
}

interface FormState {
  templateId: string;
  name: string;
  values: Record<string, string>;
}

export function PresetFormDialog({
  open,
  onOpenChange,
  initial,
  seed,
  scope,
}: PresetFormDialogProps) {
  const { data: templates } = usePromptTemplates({ scope });
  const createMutation = useCreatePromptPreset();
  const updateMutation = useUpdatePromptPreset();
  const [form, setForm] = useState<FormState>({ templateId: '', name: '', values: {} });
  const [submitting, setSubmitting] = useState(false);

  const isEdit = !!initial;

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        templateId: initial.templateId,
        name: initial.name,
        values: { ...initial.values },
      });
    } else if (seed) {
      setForm({ templateId: seed.templateId, name: '', values: { ...seed.values } });
    } else {
      setForm({ templateId: templates?.[0]?.id ?? '', name: '', values: {} });
    }
  }, [open, initial, seed, templates]);

  const selectedTemplate = useMemo(
    () => templates?.find((t) => t.id === form.templateId),
    [templates, form.templateId],
  );

  const handleSubmit = async () => {
    if (!form.templateId) {
      toast.error('テンプレートを選択してください');
      return;
    }
    if (!form.name.trim()) {
      toast.error('プレセット名は必須です');
      return;
    }
    if (!selectedTemplate) {
      toast.error('テンプレート情報の取得に失敗しました');
      return;
    }
    setSubmitting(true);
    try {
      // scope は親 template から取得（preset は単独で scope を持つが、UI からは編集不可）
      const payload: Partial<PromptPreset> = {
        templateId: form.templateId,
        scope: selectedTemplate.scope,
        name: form.name.trim(),
        values: form.values,
      };
      if (isEdit && initial) {
        await updateMutation.mutateAsync({ id: initial.id, data: payload });
        toast.success('プレセットを更新しました');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('プレセットを作成しました');
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(`保存に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'プレセット編集' : '新規プレセット作成'}
          </DialogTitle>
          <DialogDescription>
            テンプレートの入力値セットを保存して再利用できます。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>テンプレート</Label>
              <Select
                value={form.templateId}
                onValueChange={(v) => setForm((s) => ({ ...s, templateId: v, values: {} }))}
                disabled={isEdit}
              >
                <SelectTrigger>
                  <SelectValue placeholder="選択..." />
                </SelectTrigger>
                <SelectContent>
                  {templates?.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="preset-name">プレセット名</Label>
              <Input
                id="preset-name"
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                placeholder="例: 春キャンペーン共通"
                autoFocus
              />
            </div>
          </div>

          {selectedTemplate && selectedTemplate.fields.length > 0 && (
            <div className="space-y-3 pt-3 border-t border-[var(--border)]">
              <Label className="mb-0 font-semibold">入力値</Label>
              {selectedTemplate.fields.map((f) => (
                <div key={f.id}>
                  <Label htmlFor={`val-${f.id}`} className="text-xs">
                    {f.label}
                  </Label>
                  {f.type === 'textarea' ? (
                    <Textarea
                      id={`val-${f.id}`}
                      value={form.values[f.id] ?? ''}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          values: { ...s.values, [f.id]: e.target.value },
                        }))
                      }
                      placeholder={f.placeholder}
                      rows={3}
                    />
                  ) : f.type === 'select' ? (
                    <Select
                      value={form.values[f.id] ?? ''}
                      onValueChange={(v) =>
                        setForm((s) => ({ ...s, values: { ...s.values, [f.id]: v } }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={f.placeholder || '選択...'} />
                      </SelectTrigger>
                      <SelectContent>
                        {f.options.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id={`val-${f.id}`}
                      value={form.values[f.id] ?? ''}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          values: { ...s.values, [f.id]: e.target.value },
                        }))
                      }
                      placeholder={f.placeholder}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {selectedTemplate && selectedTemplate.fields.length === 0 && (
            <p className="text-xs text-[var(--text-muted)] italic">
              選択したテンプレートにフィールドが定義されていません
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            キャンセル
          </Button>
          <Button type="button" variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? '保存中...' : isEdit ? '更新' : '作成'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
